import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Badge } from '@astryxdesign/core/Badge'
import { Avatar } from '@astryxdesign/core/Avatar'
import { Banner } from '@astryxdesign/core/Banner'
import { Divider } from '@astryxdesign/core/Divider'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Selector } from '@astryxdesign/core/Selector'
import { MoreMenu } from '@astryxdesign/core/MoreMenu'
import { CheckboxInput } from '@astryxdesign/core/CheckboxInput'
import { useMe, useTrip } from '#/lib/hooks'
import { ROLE_LABELS, isAdmin, type Role } from '#/lib/types'
import { api } from '#/lib/api'

export const Route = createFileRoute('/manage/people')({ component: People })

/*
  People and the links that let them in, on one screen.

  Links used to be its own menu entry, which meant deciding who could see the
  trip in one place and handing them the way in from another. A link is a way
  into the trip for a particular person, so it belongs next to that person.

  Everything is a card rather than a list row: at 390px a row with an avatar, a
  name, an email, a role badge and two buttons put four things on one line and
  none of them were readable.
*/

interface Person {
  id: string; email: string; display_name: string
  avatar_color: string; role: Role; status: 'active' | 'disabled'
}

interface Invite {
  id: string; label: string; scopes: string; invite_kind: string
  expires_at: number; revoked_at: number | null
  last_used_at: number | null; use_count: number
}

const KIND_LABEL: Record<string, string> = {
  viewer: 'Read-only', co_traveller: 'Co-traveller', admin: 'Admin',
}

function People() {
  const me = useMe()
  const trip = useTrip()
  const [people, setPeople] = useState<Person[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'co_traveller' | 'admin'>('co_traveller')
  const [guestLabel, setGuestLabel] = useState('')
  const [withNotes, setWithNotes] = useState(false)
  const [fresh, setFresh] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    const r = await api<{ people: Person[] }>('/api/people')
    if (r) setPeople(r.people ?? [])
  }

  async function loadInvites() {
    if (!trip) return
    const r = await api<{ invites: Invite[] }>(`/api/invites?tripId=${trip.id}`)
    if (r) setInvites(r.invites ?? [])
  }

  useEffect(() => { void load() }, [])
  useEffect(() => { void loadInvites() }, [trip?.id])

  if (me && !isAdmin(me.role)) {
    return <Text type="supporting">Admins only.</Text>
  }

  async function add() {
    if (!name.trim()) return
    setError(null)
    const r = await api<{ id: string }>('/api/people', {
      method: 'POST',
      body: JSON.stringify({ displayName: name.trim(), email: email.trim(), role }),
    })
    if (!r) { setError('Could not add them. An admin needs an email address.'); return }
    setName(''); setEmail('')
    void load()
  }

  async function remove(id: string) {
    setError(null)
    const r = await api(`/api/people/${id}`, { method: 'DELETE' })
    if (!r) setError('That person cannot be removed.')
    void load()
  }

  /** A co-traveller signs in with a link; an admin claims the role with one. */
  async function invite(p: Person) {
    if (!trip) return
    setError(null)
    const r = await api<{ url: string }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify({
        tripId: trip.id,
        label: p.display_name,
        kind: p.role === 'admin' ? 'admin' : 'co_traveller',
        userId: p.id,
      }),
    })
    if (r?.url) { setFresh(r.url); void loadInvites() }
    else setError('Only the super admin can invite an admin.')
  }

  /** Read-only, for someone who is not on the trip. */
  async function createGuestLink() {
    if (!trip || !guestLabel.trim()) return
    const r = await api<{ url: string }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify({
        tripId: trip.id,
        label: guestLabel.trim(),
        kind: 'viewer',
        scopes: withNotes ? ['itinerary', 'notes'] : ['itinerary'],
        expiresAt: new Date(`${trip.end_date}T00:00:00`).getTime() + 30 * 864e5,
      }),
    })
    if (r?.url) { setFresh(r.url); setGuestLabel(''); void loadInvites() }
  }

  async function revoke(id: string) {
    setInvites((prev) => prev.map((i) => (i.id === id ? { ...i, revoked_at: Date.now() } : i)))
    await api(`/api/invites/${id}/revoke`, { method: 'POST' })
    void loadInvites()
  }

  const canGrantAdmin = me?.role === 'super_admin'

  return (
    <Stack direction="vertical" gap={5}>
      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">People</Text>
        <Text type="supporting">
          Admins sign in with their email. Everyone else signs in with a link
          you make here.
        </Text>
      </Stack>

      {fresh && <FreshLink url={fresh} onDismiss={() => setFresh(null)} />}

      {error && <Banner status="error" title={error} />}

      {/*
        One card per person, and one action per row.

        "Create admin link" and "Remove" used to share a line with the role
        badge. At 390px that put a long label, a short one and a pill on one
        row: the two buttons ended up shoulder to shoulder with no room between
        them, and the destructive one was the easier of the two to hit by
        accident. Making the link is the thing you came here to do, so it gets
        the full width. Removing someone is rare and unrecoverable, so it goes
        behind the overflow menu, which opens as a bottom sheet on a phone and
        puts the destructive item where a stray thumb is not.
      */}
      <Stack direction="vertical" gap={3}>
        {people.map((p) => {
          const manageable = p.status === 'active' && p.role !== 'super_admin'
          return (
            <Card key={p.id} padding={3}>
              <Stack direction="vertical" gap={3}>
                <Stack direction="horizontal" align="center" justify="between" gap={2}>
                  <Stack direction="horizontal" align="center" gap={3}>
                    <Avatar name={p.display_name} />
                    <Stack direction="vertical" gap={0}>
                      <Text weight="semibold" maxLines={1}>{p.display_name}</Text>
                      <Text type="supporting" maxLines={1}>
                        {p.status === 'disabled' ? 'Removed'
                          : p.email.startsWith('link:') ? 'Signs in by link'
                          : p.email}
                      </Text>
                    </Stack>
                  </Stack>
                  {manageable && (
                    <MoreMenu
                      label={`More for ${p.display_name}`}
                      alignment="end"
                      presentation="adaptive"
                      items={[{
                        label: 'Remove from the trip',
                        description: 'Their links stop working straight away.',
                        variant: 'destructive',
                        onClick: () => remove(p.id),
                      }]}
                    />
                  )}
                </Stack>

                <Stack direction="horizontal" align="center" gap={2} wrap="wrap">
                  <Badge
                    label={ROLE_LABELS[p.role]}
                    variant={p.role === 'super_admin' ? 'purple' : p.role === 'admin' ? 'blue' : 'neutral'}
                  />
                  {p.role === 'super_admin' && (
                    <Text type="supporting">Cannot be removed</Text>
                  )}
                </Stack>

                {manageable && (
                  <Button
                    label={p.role === 'admin' ? 'Create admin link' : 'Create sign-in link'}
                    variant="secondary"
                    width="100%"
                    onClick={() => invite(p)}
                  />
                )}
              </Stack>
            </Card>
          )
        })}
        {people.length === 0 && <Text type="supporting">Nobody yet.</Text>}
      </Stack>

      <Card>
        <Stack direction="vertical" gap={3}>
          <Text type="label" color="secondary">Add someone</Text>
          <TextInput label="Name" value={name} onChange={setName} placeholder="Papa" />
          <Selector
            label="Role"
            value={role}
            onChange={(v) => setRole(v as 'co_traveller' | 'admin')}
            options={[
              { value: 'co_traveller', label: 'Co-traveller', description: 'Signs in by link; adds expenses' },
              ...(canGrantAdmin
                ? [{ value: 'admin', label: 'Admin', description: 'Signs in by email through Access' }]
                : []),
            ]}
          />
          {role === 'admin' && (
            <TextInput
              label="Email"
              value={email}
              onChange={setEmail}
              placeholder="them@example.com"
              description="Must match the address you add to the Cloudflare Access policy."
            />
          )}
          <Button label="Add" variant="primary" width="100%" isDisabled={!name.trim()} onClick={add} />
        </Stack>
      </Card>

      {!canGrantAdmin && (
        <Text type="supporting">Only the super admin can add or remove admins.</Text>
      )}

      <Divider />

      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">Guest links</Text>
        <Text type="supporting">
          For someone who is not travelling. A guest link shows the itinerary
          and nothing else — no prices, no booking references.
        </Text>
      </Stack>

      <Card>
        <Stack direction="vertical" gap={3}>
          <TextInput
            label="Who is this for?"
            value={guestLabel}
            onChange={setGuestLabel}
            placeholder="Ramesh"
          />
          <CheckboxInput
            label="Include notes marked visible to guests"
            value={withNotes}
            onChange={setWithNotes}
          />
          <Button
            label="Create guest link"
            variant="primary"
            width="100%"
            isDisabled={!guestLabel.trim()}
            onClick={createGuestLink}
          />
        </Stack>
      </Card>

      {/* Every link ever issued, sign-in links included: revoking is the only
          way back once one has been sent, so it has to be one tap from here. */}
      <Stack direction="vertical" gap={3}>
        <Text type="label" color="secondary">Links issued</Text>
        {invites.length === 0 && <Text type="supporting">None yet.</Text>}
        {invites.map((i) => {
          const dead = i.revoked_at !== null || i.expires_at < Date.now()
          return (
            <Card key={i.id} padding={3}>
              <Stack direction="vertical" gap={2}>
                <Stack direction="horizontal" align="center" justify="between" gap={2}>
                  <Text weight="medium" maxLines={1}>{i.label}</Text>
                  <Badge
                    label={KIND_LABEL[i.invite_kind] ?? i.invite_kind}
                    variant={i.invite_kind === 'admin' ? 'blue' : i.invite_kind === 'co_traveller' ? 'teal' : 'neutral'}
                  />
                </Stack>
                <Text type="supporting">
                  {i.revoked_at ? 'Revoked'
                    : i.expires_at < Date.now() ? 'Expired'
                    : `Expires ${new Date(i.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                  {' · '}
                  {i.use_count === 0 ? 'never opened' : `opened ${i.use_count}×`}
                </Text>
                {dead
                  ? <Badge label="Dead" variant="neutral" />
                  : <Button label="Revoke" variant="destructive" width="100%" onClick={() => revoke(i.id)} />}
              </Stack>
            </Card>
          )
        })}
      </Stack>
    </Stack>
  )
}

/**
 * The one moment the raw token exists.
 *
 * The button that produced this used to say "Send" — it sends nothing. The
 * Worker mints a token, hands it back once and stores only its hash, and what
 * happens next is entirely up to whoever is holding the phone. So the banner
 * says what it is and offers the two things you can actually do with it.
 *
 * Copy is primary and confirms itself: a button that silently writes to the
 * clipboard is indistinguishable from a button that did nothing. Share is
 * offered only where the browser has a share sheet, which on a phone is the
 * real "send" — straight into WhatsApp — and on a desktop does not exist.
 *
 * The URL stays on screen and selectable either way, because clipboard writes
 * fail in more situations than people expect and the only copy of this link is
 * the one in front of you.
 */
function FreshLink({ url, onDismiss }: { url: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setFailed(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Insecure context, a denied permission, or no clipboard API at all.
      setFailed(true)
    }
  }

  return (
    <Banner
      status="info"
      title="Copy this now — it is not shown again"
      // Banner hides its children behind a disclosure by default, which put
      // the link and the only button that copies it one tap out of sight on
      // the one screen where there is no second chance to see them.
      collapsible={false}
    >
      <Stack direction="vertical" gap={3}>
        <Text type="code">{url}</Text>
        <Stack direction="horizontal" gap={2} wrap="wrap">
          <Button
            label={copied ? 'Copied' : 'Copy link'}
            variant="primary"
            clickAction={copy}
          />
          {canShare && (
            <Button
              label="Share…"
              variant="secondary"
              clickAction={() => navigator.share({ url }).catch(() => {})}
            />
          )}
          <Button label="Done" variant="ghost" onClick={onDismiss} />
        </Stack>
        {failed && (
          <Text type="supporting">
            This browser would not let the page write to the clipboard. Select
            the link above and copy it by hand.
          </Text>
        )}
      </Stack>
    </Banner>
  )
}
